<template>
  <div class="tasks-dashboard">
    <!-- 侧边栏 -->
    <aside class="sidebar" :class="{ collapsed: sidebarCollapsed }">
      <div class="sidebar-header">
        <div class="logo" @click="$router.push('/dashboard')">
          <i class="fas fa-graduation-cap"></i>
          <span v-show="!sidebarCollapsed">智慧迎新</span>
        </div>
        <button class="toggle-btn" @click="sidebarCollapsed = !sidebarCollapsed">
          <i :class="sidebarCollapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-left'"></i>
        </button>
      </div>
      <nav class="sidebar-nav">
        <a href="#" @click.prevent="$router.push('/dashboard')" :class="{ active: $route.path === '/dashboard' }">
          <i class="fas fa-tachometer-alt"></i>
          <span v-show="!sidebarCollapsed">仪表盘</span>
        </a>
        <a href="#" @click.prevent="$router.push('/tasks')" :class="{ active: $route.path === '/tasks' }">
          <i class="fas fa-tasks"></i>
          <span v-show="!sidebarCollapsed">我的任务</span>
        </a>
        <a href="#" @click.prevent="$router.push('/mall')" :class="{ active: $route.path === '/mall' }">
          <i class="fas fa-store"></i>
          <span v-show="!sidebarCollapsed">积分商城</span>
        </a>
        <a href="#" @click.prevent="openProfile">
          <i class="fas fa-user-circle"></i>
          <span v-show="!sidebarCollapsed">个人资料</span>
        </a>
      </nav>
      <div class="sidebar-footer">
        <button @click="logout" class="logout-btn">
          <i class="fas fa-sign-out-alt"></i>
          <span v-show="!sidebarCollapsed">退出登录</span>
        </button>
      </div>
    </aside>

    <!-- 主内容 -->
    <main class="main-content">
      <header class="top-bar">
        <div class="welcome">
          <h2>我的任务</h2>
          <p>完成每日任务，赚取积分奖励 🎯</p>
        </div>
        <div class="user-info">
          <div class="points-badge">
            <i class="fas fa-star"></i>
            <span>{{ userPoints }} 积分</span>
          </div>
          <div class="avatar">
            <i class="fas fa-user-circle"></i>
          </div>
        </div>
      </header>

      <!-- 统计卡片 -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon green">
            <i class="fas fa-check-double"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.completed }}</h3>
            <p>已完成</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon orange">
            <i class="fas fa-play-circle"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.inProgress }}</h3>
            <p>进行中</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon gray">
            <i class="fas fa-hourglass-start"></i>
          </div>
          <div class="stat-info">
            <h3>{{ stats.unstarted }}</h3>
            <p>未开始</p>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon purple">
            <i class="fas fa-chart-simple"></i>
          </div>
          <div class="stat-info">
            <h3>{{ completionRate }}%</h3>
            <p>完成进度</p>
          </div>
        </div>
      </div>

      <!-- 任务列表 -->
      <div class="tasks-container">
        <div class="tasks-header">
          <h3><i class="fas fa-list-check"></i> 任务清单</h3>
          <button @click="refreshTasks" class="refresh-btn">
            <i class="fas fa-sync-alt"></i> 刷新
          </button>
        </div>

        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i> 加载中...
        </div>

        <div v-else-if="tasks.length === 0" class="empty-state">
          <i class="fas fa-inbox"></i>
          <p>暂无任务</p>
        </div>

        <div v-else class="task-list">
          <div v-for="task in tasks" :key="task.id" class="task-card">
            <div class="task-icon" :class="getStatusClass(task.status)">
              <i :class="getStatusIcon(task.status)"></i>
            </div>
            <div class="task-content">
              <div class="task-title">
                <span class="task-name">{{ task.name }}</span>
                <span class="task-points">+{{ task.points }} 积分</span>
              </div>
              <div class="task-desc">{{ task.description }}</div>
              <div class="task-meta">
                <span class="task-status" :class="getStatusClass(task.status)">
                  <i :class="getStatusIcon(task.status)"></i> {{ task.status }}
                </span>
              </div>
            </div>
            <div class="task-actions">
              <button
                v-if="task.status === '未开始'"
                @click="startTask(task)"
                class="action-btn start"
              >
                <i class="fas fa-play"></i> 开始
              </button>
              <button
                v-else-if="task.status === '进行中'"
                @click="completeTask(task)"
                class="action-btn complete"
              >
                <i class="fas fa-check"></i> 完成
              </button>
              <div v-else class="completed-badge">
                <i class="fas fa-check-circle"></i> 已完成
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 弹窗：编辑个人资料 -->
    <div v-if="profileDialog" class="modal-overlay" @click="profileDialog = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>编辑个人信息</h3>
          <button @click="profileDialog = false" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>用户名</label>
            <input v-model="editForm.username" disabled>
          </div>
          <div class="form-group">
            <label>邮箱</label>
            <input v-model="editForm.email" type="email">
          </div>
          <div class="form-group">
            <label>手机号</label>
            <input v-model="editForm.phone" type="tel">
          </div>
        </div>
        <div class="modal-footer">
          <button @click="profileDialog = false" class="cancel-btn">取消</button>
          <button @click="saveProfile" class="save-btn">保存</button>
        </div>
      </div>
    </div>

    <!-- 弹窗：退出确认 -->
    <div v-if="logoutDialog" class="modal-overlay" @click="logoutDialog = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>确认退出</h3>
          <button @click="logoutDialog = false" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <p>确定要退出登录吗？</p>
        </div>
        <div class="modal-footer">
          <button @click="logoutDialog = false" class="cancel-btn">取消</button>
          <button @click="confirmLogout" class="save-btn danger">退出</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TasksView',
  data() {
    return {
      sidebarCollapsed: false,
      activeMenu: 'tasks',
      loading: false,
      tasks: [],
      stats: { completed: 0, inProgress: 0, unstarted: 0, total: 0 },
      userPoints: 0,
      profileDialog: false,
      logoutDialog: false,
      editForm: { username: '', email: '', phone: '' },
      userInfo: {}
    }
  },
  computed: {
    completionRate() {
      if (this.stats.total === 0) return 0
      return Math.round((this.stats.completed / this.stats.total) * 100)
    }
  },
  created() {
    this.loadUserInfo()
    this.loadData()
  },
  methods: {
    getStatusIcon(status) {
      const icons = {
        '已完成': 'fas fa-check-circle',
        '进行中': 'fas fa-spinner fa-pulse',
        '未开始': 'fas fa-circle'
      }
      return icons[status] || 'fas fa-question'
    },
    getStatusClass(status) {
      const classes = {
        '已完成': 'status-completed',
        '进行中': 'status-progress',
        '未开始': 'status-pending'
      }
      return classes[status] || ''
    },
    goToMall() {
      this.$router.push('/mall')
    },
    loadUserInfo() {
      const userStr = localStorage.getItem('user')
      if (userStr) {
        this.userInfo = JSON.parse(userStr)
        this.editForm = { ...this.userInfo }
      } else {
        this.$router.push('/login')
      }
    },
    async loadData() {
      this.loading = true
      try {
        const token = localStorage.getItem('token')
        const [tasksRes, statsRes, pointsRes] = await Promise.all([
          this.$axios.get('/api/tasks/my', { headers: { Authorization: `Bearer ${token}` } }),
          this.$axios.get('/api/tasks/stats', { headers: { Authorization: `Bearer ${token}` } }),
          this.$axios.get('/api/points/my', { headers: { Authorization: `Bearer ${token}` } })
        ])
        if (tasksRes.data.code === 200) this.tasks = tasksRes.data.data
        if (statsRes.data.code === 200) this.stats = statsRes.data.data
        if (pointsRes.data.code === 200) this.userPoints = pointsRes.data.data.points
      } catch (error) {
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },
    async refreshTasks() {
      await this.loadData()
      this.$message.success('刷新成功')
    },
    async startTask(task) {
      try {
        const token = localStorage.getItem('token')
        const res = await this.$axios.post(`/api/tasks/${task.id}/start`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.data.code === 200) {
          task.status = '进行中'
          this.stats.inProgress++
          this.stats.unstarted--
          this.$message.success('任务已开始，完成后记得点击完成获取积分！')
          await this.loadData()
        }
      } catch (error) {
        this.$message.error('开始任务失败')
      }
    },
    async completeTask(task) {
      this.$confirm(`确定完成任务"${task.name}"吗？将获得 ${task.points} 积分！`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'success'
      }).then(async () => {
        try {
          const token = localStorage.getItem('token')
          const res = await this.$axios.post(`/api/tasks/${task.id}/complete`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
          if (res.data.code === 200) {
            task.status = '已完成'
            this.$message.success(res.data.message)
            await this.loadData()
          } else {
            this.$message.error(res.data.message)
          }
        } catch (error) {
          this.$message.error('完成任务失败')
        }
      }).catch(() => {})
    },
    goToDashboard() {
      this.$router.push('/dashboard')
    },
    openProfile() {
      this.profileDialog = true
    },
    async saveProfile() {
      try {
        const token = localStorage.getItem('token')
        const res = await this.$axios.put('/api/user/profile',
          { email: this.editForm.email, phone: this.editForm.phone },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        if (res.data.code === 200) {
          this.userInfo = { ...this.editForm }
          localStorage.setItem('user', JSON.stringify(this.userInfo))
          this.profileDialog = false
          this.$message.success('保存成功')
        }
      } catch (error) {
        this.$message.error('保存失败')
      }
    },
    logout() {
      this.logoutDialog = true
    },
    confirmLogout() {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.tasks-dashboard {
  display: flex;
  min-height: 100vh;
  background: #f5f7fb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 侧边栏样式 - 与 Dashboard 保持一致 */
.sidebar {
  width: 260px;
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
  color: #fff;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  z-index: 100;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.logo i {
  font-size: 28px;
  color: #667eea;
}

.logo span {
  font-size: 18px;
  font-weight: 600;
}

.toggle-btn {
  background: rgba(255,255,255,0.1);
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-nav {
  flex: 1;
  padding: 20px 0;
}

.sidebar-nav a {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 20px;
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: all 0.3s;
  margin: 4px 12px;
  border-radius: 12px;
  cursor: pointer;
}

.sidebar-nav a i {
  width: 20px;
  font-size: 18px;
}

.sidebar-nav a:hover,
.sidebar-nav a.active {
  background: rgba(102, 126, 234, 0.2);
  color: #667eea;
}

.sidebar-footer {
  padding: 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.logout-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255,255,255,0.05);
  border: none;
  color: rgba(255,255,255,0.7);
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.logout-btn:hover {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

/* 主内容区 */
.main-content {
  flex: 1;
  margin-left: 260px;
  transition: margin-left 0.3s ease;
}

.sidebar.collapsed + .main-content,
.tasks-dashboard .sidebar.collapsed ~ .main-content {
  margin-left: 70px;
}

.top-bar {
  background: white;
  padding: 20px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.welcome h2 {
  font-size: 20px;
  margin-bottom: 4px;
}

.welcome p {
  color: #888;
  font-size: 14px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.points-badge {
  background: linear-gradient(135deg, #ffd89b, #c7e9fb);
  padding: 8px 16px;
  border-radius: 30px;
  color: #333;
  font-weight: 600;
}

.points-badge i {
  color: #ffc107;
  margin-right: 6px;
}

.avatar i {
  font-size: 40px;
  color: #667eea;
}

/* 统计卡片 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  padding: 30px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon.green { background: #e8f5e9; color: #4caf50; }
.stat-icon.orange { background: #fff3e0; color: #ff9800; }
.stat-icon.gray { background: #f5f5f5; color: #9e9e9e; }
.stat-icon.purple { background: #f3e5f5; color: #9c27b0; }

.stat-icon i {
  font-size: 24px;
}

.stat-info h3 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 4px;
}

.stat-info p {
  color: #888;
  font-size: 13px;
}

/* 任务容器 */
.tasks-container {
  background: white;
  margin: 0 30px 30px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

.tasks-header {
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tasks-header h3 {
  margin: 0;
}

.tasks-header h3 i {
  margin-right: 8px;
  color: #667eea;
}

.refresh-btn {
  background: #f5f5f5;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.refresh-btn i {
  margin-right: 5px;
}

.refresh-btn:hover {
  background: #e8e8e8;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 60px;
  color: #999;
}

.loading-state i, .empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
}

/* 任务卡片 */
.task-list {
  padding: 0 20px;
}

.task-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 16px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
}

.task-card:hover {
  background: #fafafa;
}

.task-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.task-icon.status-completed { background: #e8f5e9; color: #4caf50; }
.task-icon.status-progress { background: #fff3e0; color: #ff9800; }
.task-icon.status-pending { background: #f5f5f5; color: #bdbdbd; }

.task-icon i {
  font-size: 20px;
}

.task-content {
  flex: 1;
}

.task-title {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 6px;
  flex-wrap: wrap;
  gap: 8px;
}

.task-name {
  font-weight: 600;
  font-size: 16px;
}

.task-points {
  font-size: 13px;
  color: #ff9800;
  font-weight: 500;
}

.task-desc {
  font-size: 13px;
  color: #888;
  margin-bottom: 8px;
}

.task-meta {
  display: flex;
  gap: 12px;
  align-items: center;
}

.task-status {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.task-status.status-completed {
  background: #e8f5e9;
  color: #4caf50;
}

.task-status.status-progress {
  background: #fff3e0;
  color: #ff9800;
}

.task-status.status-pending {
  background: #f5f5f5;
  color: #9e9e9e;
}

.task-actions {
  min-width: 100px;
  text-align: right;
}

.action-btn {
  padding: 8px 20px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
}

.action-btn.start {
  background: #e3f2fd;
  color: #2196f3;
}

.action-btn.start:hover {
  background: #bbdef5;
  transform: translateY(-1px);
}

.action-btn.complete {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.action-btn.complete:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.completed-badge {
  color: #4caf50;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  justify-content: flex-end;
}

.completed-badge i {
  font-size: 18px;
}

/* 弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 450px;
  animation: modalFade 0.2s ease;
}

@keyframes modalFade {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 24px;
}

.modal-body .form-group {
  margin-bottom: 16px;
}

.modal-body label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.modal-body input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 10px;
  box-sizing: border-box;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-btn, .save-btn {
  padding: 10px 24px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
}

.cancel-btn {
  background: #f5f5f5;
}

.save-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.save-btn.danger {
  background: #dc3545;
}
</style>